import { useState, type ReactNode } from 'react';

import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsOrgAdmin } from '@/features/identity';
import type { WorkspaceSummary } from '@/lib/api/generated/model';

import { ACCESS_COPY } from '../accessCopy';
import type { WorkspaceTab } from '../searchParams';
import { sectionOf, type WorkspaceSection } from '../sections';

const PANEL_ID = 'workspace-tab-panel';

/** Tab order, which is also the order a search looks for a match in. */
const TABS: {
  value: WorkspaceTab;
  section: WorkspaceSection;
  label: string;
}[] = [
  { value: 'yours', section: 'yours', label: ACCESS_COPY.yoursTab },
  { value: 'organization', section: 'shared', label: ACCESS_COPY.sharedTab },
  { value: 'others', section: 'others', label: ACCESS_COPY.othersTab },
];

/**
 * The list, divided by where you stand once access is on (spec 200): your
 * workspaces, those shared with the organization, and — for an org admin — every
 * other one, one at a time under a tab strip with counts. It was three stacked
 * sections, whose small headings did not separate them, so the page read as one
 * long grid of 60-odd cards. Off, it is the one grid it always was.
 *
 * The open tab is the address's (`?tab=`), so a link and Back land on it. Under a
 * search the counts are the matches, and a tab with none gives way to the first
 * that has some — unless it was picked by hand during that search, which a reader
 * means. Clearing the search returns to the address's tab.
 */
export function WorkspaceTabs({
  all,
  entries,
  grid,
  currentUserId,
  query = '',
  tab,
  onTabChange,
  count,
  sortMenu,
}: {
  /** Every workspace read, before the search: decides which tabs exist. */
  all: readonly WorkspaceSummary[];
  /** The ones the search kept, in the list's order. */
  entries: readonly WorkspaceSummary[];
  grid: (entries: WorkspaceSummary[]) => ReactNode;
  currentUserId: string | undefined;
  /** The search the entries were narrowed by; empty when they were not. */
  query?: string;
  /** The tab the address asks for, if any. */
  tab: WorkspaceTab | undefined;
  onTabChange: (tab: WorkspaceTab) => void;
  /** "62 workspaces": shown only while there are no tabs to carry the counts. */
  count: ReactNode;
  /** Null on a phone, where Sort sits beside the search field. */
  sortMenu: ReactNode;
}) {
  const isOrgAdmin = useIsOrgAdmin();
  // A tab picked during a search, for that search only.
  const [byHand, setByHand] = useState<{
    query: string;
    tab: WorkspaceTab;
  } | null>(null);
  const searching = query.trim() !== '';

  const totals = divide(all, currentUserId);
  if (totals === null) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          {count}
          {sortMenu}
        </div>
        {grid([...entries])}
      </div>
    );
  }
  const matches = divide(entries, currentUserId) ?? {
    yours: [],
    shared: [],
    others: [],
  };

  // Yours always; the others only when they hold something. "All other
  // workspaces" are private ones you are not in, which only an org admin is sent.
  const offered = TABS.filter(
    ({ section }) => section === 'yours' || totals[section].length > 0,
  );
  const fallback =
    totals.yours.length === 0 && offered.length > 1
      ? (offered[1]?.value ?? 'yours')
      : 'yours';
  const asked =
    tab !== undefined && offered.some((option) => option.value === tab)
      ? tab
      : fallback;
  const open =
    byHand?.query === query
      ? byHand.tab
      : searching && matches[sectionFor(asked)].length === 0
        ? (offered.find(({ section }) => matches[section].length > 0)?.value ??
          asked)
        : asked;
  const openSection = sectionFor(open);
  const shown = matches[openSection];

  return (
    <div className="flex flex-col gap-4">
      <div className="border-border flex items-end justify-between gap-3 border-b">
        {/* On a phone the tabs share the width evenly, and Sort has moved to
            the search row (spec 267). */}
        <TabsList
          aria-label="Workspaces"
          className="min-w-0 gap-6 max-md:flex-1 max-md:gap-2"
        >
          {offered.map(({ value, section, label }) => {
            const n = matches[section].length;
            return (
              <TabsTrigger
                key={value}
                id={`workspace-tab-${value}`}
                selected={open === value}
                panelId={PANEL_ID}
                count={n}
                aria-label={
                  searching
                    ? `${label}, ${String(n)} matching`
                    : `${label}, ${String(n)} ${n === 1 ? 'workspace' : 'workspaces'}`
                }
                className="touch-target pb-2 text-sm max-md:flex-1 max-md:justify-center"
                onClick={() => {
                  if (searching) setByHand({ query, tab: value });
                  if (value !== open) onTabChange(value);
                }}
              >
                {label}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {sortMenu ? <div className="shrink-0 pb-1">{sortMenu}</div> : null}
      </div>

      <div
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={`workspace-tab-${open}`}
        className="flex flex-col gap-3"
      >
        {openSection === 'shared' ? (
          <p className="text-muted-foreground text-xs">
            {isOrgAdmin ? ACCESS_COPY.sharedHintAdmin : ACCESS_COPY.sharedHint}
          </p>
        ) : openSection === 'others' ? (
          <p className="text-muted-foreground text-xs">
            {ACCESS_COPY.othersHint}
          </p>
        ) : null}
        {shown.length > 0 ? (
          grid(shown)
        ) : (
          <p className="text-muted-foreground text-sm">
            {openSection !== 'yours'
              ? ACCESS_COPY.tabNoMatch
              : searching
                ? ACCESS_COPY.yoursNoMatch
                : ACCESS_COPY.yoursEmpty}
          </p>
        )}
      </div>
    </div>
  );
}

function sectionFor(tab: WorkspaceTab): WorkspaceSection {
  return TABS.find((option) => option.value === tab)?.section ?? 'yours';
}

/** The entries by section, or `null` when access is off and nothing divides. */
function divide(
  entries: readonly WorkspaceSummary[],
  currentUserId: string | undefined,
): Record<WorkspaceSection, WorkspaceSummary[]> | null {
  const sections: Record<WorkspaceSection, WorkspaceSummary[]> = {
    yours: [],
    shared: [],
    others: [],
  };
  let divided = false;
  for (const entry of entries) {
    const section = sectionOf(entry, currentUserId);
    if (section === null) continue;
    divided = true;
    sections[section].push(entry);
  }
  return divided ? sections : null;
}
